/**
 * menuPackagesSlice — single source of truth for the Menu & Packages feature.
 *
 * Holds three concerns:
 *  1. Header / navigation state — active tab, search, filter.
 *  2. Domain data — menu course groups, packages, and draft rows (seeded from
 *     demo fixtures; the add/edit/delete reducers mutate these via Immer).
 *  3. Transient UI — the detail view, the add/edit form draft, the draft
 *     drawer, the delete confirmation, the photo lightbox, and the placeholder
 *     drawer for not-yet-built tabs.
 *
 * Components never hold this state locally; they read it with `useAppSelector`
 * and dispatch the actions below. All values are serialisable (object-URL
 * strings, not File objects), so RTK's serializability checks stay happy.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  buildMenuGroups,
  type Category,
  pickAddonDrafts,
  pickMenuDrafts,
  pickPackageDrafts,
} from "@/components/menu-packages/cateringMappers";
import type {
  ActiveTab,
  AddOnItem,
  Certification,
  Course,
  Ingredient,
  MenuGroup,
  MenuItem,
  PackageCourseSection,
  PackageFormDraft,
  PackageItem,
  Venue,
} from "@/components/menu-packages/types";

export type FormMode = "add" | "edit";

export interface DraftMenu {
  id: string;
  name: string;
  courseName: string;
  price: number;
}

export interface DraftPackage {
  id: string;
  name: string;
  courseName: string;
  price: number;
}

/** Editable in-progress values for the Add/Edit menu form. */
export interface MenuFormDraft {
  menuName: string;
  /** Course option value (slug) */
  courseName: string;
  description: string;
  price: string;
  /** Price unit option value (slug) */
  unit: string;
  calories: string;
  ingredients: Ingredient[];
  certifications: Certification[];
  active: boolean;
  /** Object URL of a newly picked photo */
  photoPreview: string | null;
  /** Gradient placeholder classes (seeded in edit mode) */
  photoGradient: string | null;
}

export interface MenuPackagesState {
  // Header / navigation
  activeTab: ActiveTab;
  search: string;
  filter: string;

  // Domain data
  /** All menu items from the API (every status), the source of truth. */
  menuItems: MenuItem[];
  /** Published items grouped by course (derived from menuItems). */
  menuGroups: MenuGroup[];
  /** All packages from the API — seeded by SSR, refreshed after mutations. */
  packages: PackageItem[];
  /** Draft menu items (derived from menuItems). */
  drafts: DraftMenu[];
  /** Menu courses (taxonomy) from the API — feeds the course dropdown/filter. */
  courses: Course[];
  /** True while the menu list/courses are being fetched. */
  menuLoading: boolean;
  /** True while the packages list/categories are being fetched. */
  packagesLoading: boolean;
  /** Package categories from the API — feeds the category dropdown in the form. */
  packageCategories: Category[];
  /** Venues from the API — feeds the package "Available at Venue(s)" select. */
  venues: Venue[];

  // Detail view (full page)
  detailItem: MenuItem | null;
  detailPickedImage: string | null;

  // Add / Edit form drawer
  formOpen: boolean;
  formMode: FormMode;
  formItemId: string | null;
  /** `updated_at` of the item being edited, for optimistic-concurrency PATCH. */
  formItemUpdatedAt: string | null;
  formDraft: MenuFormDraft;
  lightboxOpen: boolean;

  // Draft drawer
  draftOpen: boolean;

  // Delete confirmation
  deleteOpen: boolean;
  deleteItemId: string | null;
  /** Which entity the pending delete targets (menu → API delete). */
  deleteKind: "menu" | "package" | "addon" | null;

  // Package form drawer (3-step: Package Info → Menu & Courses → Pricing & Review)
  pkgFormOpen: boolean;
  pkgFormMode: FormMode;
  pkgFormStep: number;
  pkgFormItemId: string | null;
  /** `updated_at` of the package being edited, for optimistic-concurrency PATCH. */
  pkgFormItemUpdatedAt: string | null;
  pkgFormDraft: PackageFormDraft;
  pkgLightboxOpen: boolean;

  // Draft packages drawer (Figma 10.13B)
  packageDrafts: DraftPackage[];
  pkgDraftOpen: boolean;

  // Full-page package details view
  packageDetailItem: PackageItem | null;
  // True while the full detail (menuStructure / venues / performance) is being
  // hydrated in the background after an optimistic open from the summary.
  packageDetailLoading: boolean;

  // Placeholder drawer (non-menu tab add actions, pre-build)
  placeholderOpen: boolean;

  // Add-ons
  addOns: AddOnItem[];
  addOnsLoading: boolean;
  addOnFormOpen: boolean;
  addOnFormMode: FormMode;
  addOnFormItemId: string | null;
  /** `updated_at` of the add-on being edited, for optimistic-concurrency PATCH. */
  addOnFormItemUpdatedAt: string | null;
  addOnFormDraft: {
    name: string;
    description: string;
    price: string;
    active: boolean;
    photoPreview: string | null;
    photoGradient: string | null;
  };
  addOnLightboxOpen: boolean;
  addOnDrafts: Array<{ id: string; name: string; price: number }>;
  addOnDraftOpen: boolean;
  addOnDetailItem: AddOnItem | null;
}

const EMPTY_INGREDIENT: Ingredient = { name: "", amount: "", unit: "gm" };

const emptyDraft = (): MenuFormDraft => ({
  menuName: "",
  courseName: "",
  description: "",
  price: "",
  unit: "per_person",
  calories: "",
  ingredients: [{ ...EMPTY_INGREDIENT }],
  certifications: [],
  active: true,
  photoPreview: null,
  photoGradient: null,
});

/**
 * Build the editable form draft from a stored MenuItem. `courseName` carries the
 * course **id** (the form select binds to ids) and `unit` carries the raw
 * `price_unit` enum — both round-tripped from the API via the optional fields.
 */
const draftFromItem = (item: MenuItem): MenuFormDraft => ({
  menuName: item.name,
  courseName: item.courseId ?? "",
  description: item.description,
  price: String(item.price ?? ""),
  unit: item.priceUnitValue ?? "per_person",
  calories: item.kcal ? String(item.kcal) : "",
  ingredients: item.ingredientList.length
    ? item.ingredientList.map((i) => ({ ...i }))
    : [{ ...EMPTY_INGREDIENT }],
  certifications: [...item.certifications],
  active: item.isActive ?? item.status === "active",
  photoPreview: null,
  photoGradient: `${item.gradientFrom} ${item.gradientTo}`,
});

// ─── Package form helpers ─────────────────────────────────────────────────────

const categoryColorMap: Record<string, PackageItem["typeBadge"]["color"]> = {
  regular: "neutral",
  wedding: "purple",
  ceremony: "info",
};

const categoryGradientMap: Record<string, { from: string; to: string }> = {
  regular: { from: "from-slate-200", to: "to-slate-400" },
  wedding: { from: "from-purple-200", to: "to-purple-400" },
  ceremony: { from: "from-orange-200", to: "to-orange-400" },
};

const emptyPkgDraft = (): PackageFormDraft => ({
  name: "",
  category: "regular",
  venues: "all",
  description: "",
  pricingType: "per-person",
  basePricePerson: "",
  flatBaseFee: "",
  courses: [{ id: "1", courseName: "", items: [] }],
  active: true,
  photoPreview: null,
  photoGradient: null,
});

/**
 * Build the form draft from an existing PackageItem for the edit flow.
 * `category` now stores the raw API category id (UUID) so the CSelect can
 * round-trip it — the form drives options from `packageCategories` in the
 * slice, not from the static PACKAGE_CATEGORY_OPTIONS.
 * pricingType is mapped from the API enum to the UI values used by PRICING_TYPE_OPTIONS.
 */
const pkgDraftFromItem = (pkg: PackageItem): PackageFormDraft => {
  // Map pricingType label back to the UI option value
  const pricingType =
    pkg.pricingType === "Per person"
      ? "per-person"
      : pkg.pricingType === "Flat fee"
        ? "flat-fee"
        : "per-person";

  return {
    name: pkg.name,
    // Preselect the category by its id (mapped from the API `category.id`); the
    // form's category options are keyed by id. Empty string if uncategorised.
    category: pkg.categoryId ?? "",
    // Preselect "all" for all-venues packages; otherwise the first scoped venue
    // (the single-select control holds one value). Falls back to "all".
    venues:
      pkg.availableAtAllVenues === false && pkg.venueIds?.length
        ? pkg.venueIds[0]
        : "all",
    description: pkg.description ?? "",
    pricingType,
    basePricePerson: String(pkg.pricePerPerson),
    flatBaseFee: "",
    courses:
      pkg.courses > 0
        ? Array.from({ length: pkg.courses }, (_, i) => ({
            id: String(i + 1),
            courseName: "",
            items: [],
          }))
        : [{ id: "1", courseName: "", items: [] }],
    active: pkg.isActive ?? pkg.status === "active",
    photoPreview: null,
    photoGradient: `${pkg.gradientFrom} ${pkg.gradientTo}`,
  };
};

/**
 * Build a minimal PackageItem from an in-progress draft for optimistic display
 * in the submitPackageForm reducer (legacy in-slice flow, kept as fallback).
 * Category is now an API UUID/label so we use draft.category directly as the
 * badge label — no static PACKAGE_CATEGORY_OPTIONS lookup needed.
 */
const pkgFromDraft = (draft: PackageFormDraft, id: string): PackageItem => {
  // Use draft.category directly — it may be an API UUID or a display label.
  const categoryLabel = draft.category || "Regular";
  const color = categoryColorMap[draft.category] ?? "neutral";
  const gradient = categoryGradientMap[draft.category] ?? {
    from: "from-slate-200",
    to: "to-slate-400",
  };
  const validCourses = draft.courses.filter((c) => c.courseName.trim());
  return {
    id,
    name: draft.name || "Untitled Package",
    tier: draft.name || "Untitled",
    pricePerPerson: Number(draft.basePricePerson) || 0,
    courses: validCourses.length,
    items: draft.courses.reduce((sum, c) => sum + c.items.length, 0),
    description: draft.description || undefined,
    typeBadge: { label: categoryLabel, color },
    status: draft.active ? "active" : "draft",
    gradientFrom: gradient.from,
    gradientTo: gradient.to,
  };
};

// ─── Add-on form helpers ──────────────────────────────────────────────────────

const emptyAddOnDraft = () => ({
  name: "",
  description: "",
  price: "",
  active: true,
  photoPreview: null as string | null,
  photoGradient: null as string | null,
});

const addOnDraftFromItem = (
  item: AddOnItem,
): ReturnType<typeof emptyAddOnDraft> => ({
  name: item.name,
  description: item.description,
  price: String(item.price),
  active: item.isActive ?? item.status === "active",
  photoPreview: null,
  photoGradient: `${item.gradientFrom} ${item.gradientTo}`,
});

const addOnFromDraft = (
  draft: ReturnType<typeof emptyAddOnDraft>,
  id: string,
): AddOnItem => ({
  id,
  name: draft.name || "Untitled Add-on",
  description: draft.description,
  price: Number(draft.price) || 0,
  status: draft.active ? "active" : "draft",
  gradientFrom: "from-neutral-100",
  gradientTo: "to-neutral-200",
});

// ─────────────────────────────────────────────────────────────────────────────

const initialState: MenuPackagesState = {
  activeTab: "menu",
  search: "",
  filter: "all",
  menuItems: [],
  menuGroups: [],
  // Packages and add-ons are now API-backed; start empty and let SSR hydrate.
  packages: [],
  drafts: [],
  courses: [],
  menuLoading: true,
  // True until SSR data arrives for packages/categories.
  packagesLoading: true,
  packageCategories: [],
  venues: [],
  detailItem: null,
  detailPickedImage: null,
  formOpen: false,
  formMode: "add",
  formItemId: null,
  formItemUpdatedAt: null,
  formDraft: emptyDraft(),
  lightboxOpen: false,
  draftOpen: false,
  deleteOpen: false,
  deleteItemId: null,
  deleteKind: null,
  pkgFormOpen: false,
  pkgFormMode: "add" as FormMode,
  pkgFormStep: 0,
  pkgFormItemId: null,
  pkgFormItemUpdatedAt: null,
  pkgFormDraft: emptyPkgDraft(),
  pkgLightboxOpen: false,
  packageDrafts: [],
  pkgDraftOpen: false,
  packageDetailItem: null,
  packageDetailLoading: false,
  placeholderOpen: false,

  // Add-ons — now API-backed; start empty and let SSR hydrate.
  addOns: [],
  // True until SSR data arrives.
  addOnsLoading: true,
  addOnFormOpen: false,
  addOnFormMode: "add" as FormMode,
  addOnFormItemId: null,
  addOnFormItemUpdatedAt: null,
  addOnFormDraft: emptyAddOnDraft(),
  addOnLightboxOpen: false,
  addOnDrafts: [],
  addOnDraftOpen: false,
  addOnDetailItem: null,
};

const menuPackagesSlice = createSlice({
  name: "menuPackages",
  initialState,
  reducers: {
    setActiveTab(state, action: PayloadAction<ActiveTab>) {
      state.activeTab = action.payload;
      state.filter = "all";
    },
    setSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    },
    setFilter(state, action: PayloadAction<string>) {
      state.filter = action.payload;
    },

    // ── Menu data (from API) ───────────────────────────────────────────────
    setMenuLoading(state, action: PayloadAction<boolean>) {
      state.menuLoading = action.payload;
    },
    setCourses(state, action: PayloadAction<Course[]>) {
      state.courses = action.payload;
      // Re-derive groups so course ordering reflects the latest taxonomy.
      state.menuGroups = buildMenuGroups(
        state.menuItems,
        state.courses.map((c) => c.name),
      );
    },
    setMenuItems(state, action: PayloadAction<MenuItem[]>) {
      state.menuItems = action.payload;
      state.menuGroups = buildMenuGroups(
        action.payload,
        state.courses.map((c) => c.name),
      );
      state.drafts = pickMenuDrafts(action.payload);
    },
    /**
     * Replace (or insert) a single menu item after an edit — lets the grid
     * reflect a change instantly without a full-list refetch. Re-derives the
     * course groups + draft rows from the patched list.
     */
    upsertMenuItem(state, action: PayloadAction<MenuItem>) {
      const item = action.payload;
      const idx = state.menuItems.findIndex((m) => m.id === item.id);
      if (idx !== -1) state.menuItems[idx] = item;
      else state.menuItems.unshift(item);
      state.menuGroups = buildMenuGroups(
        state.menuItems,
        state.courses.map((c) => c.name),
      );
      state.drafts = pickMenuDrafts(state.menuItems);
    },

    // ── Packages data (from API) ───────────────────────────────────────────
    /** Replace the packages list; derives packageDrafts from the new data. */
    setPackages(state, action: PayloadAction<PackageItem[]>) {
      state.packages = action.payload;
      // Derive draft package rows from the API-sourced list.
      state.packageDrafts = pickPackageDrafts(action.payload);
    },
    /** Replace (or insert) a single package after an edit — avoids a full
     *  refetch. Re-derives the draft package rows. */
    upsertPackage(state, action: PayloadAction<PackageItem>) {
      const pkg = action.payload;
      const idx = state.packages.findIndex((p) => p.id === pkg.id);
      if (idx !== -1) state.packages[idx] = pkg;
      else state.packages.unshift(pkg);
      state.packageDrafts = pickPackageDrafts(state.packages);
    },
    setPackagesLoading(state, action: PayloadAction<boolean>) {
      state.packagesLoading = action.payload;
    },
    /** Replace the package categories list (for the form dropdown). */
    setPackageCategories(state, action: PayloadAction<Category[]>) {
      state.packageCategories = action.payload;
    },
    /** Replace the venues list (for the "Available at Venue(s)" select). */
    setVenues(state, action: PayloadAction<Venue[]>) {
      state.venues = action.payload;
    },

    // ── Add-ons data (from API) ────────────────────────────────────────────
    /** Replace the add-ons list; derives addOnDrafts from the new data. */
    setAddOns(state, action: PayloadAction<AddOnItem[]>) {
      state.addOns = action.payload;
      // Derive draft add-on rows from the API-sourced list.
      state.addOnDrafts = pickAddonDrafts(action.payload);
    },
    /** Replace (or insert) a single add-on after an edit — avoids a full
     *  refetch. Re-derives the draft add-on rows. */
    upsertAddOn(state, action: PayloadAction<AddOnItem>) {
      const addon = action.payload;
      const idx = state.addOns.findIndex((a) => a.id === addon.id);
      if (idx !== -1) state.addOns[idx] = addon;
      else state.addOns.unshift(addon);
      state.addOnDrafts = pickAddonDrafts(state.addOns);
    },
    setAddOnsLoading(state, action: PayloadAction<boolean>) {
      state.addOnsLoading = action.payload;
    },

    // Detail view
    openDetail(state, action: PayloadAction<MenuItem>) {
      state.detailItem = action.payload;
      state.detailPickedImage = null;
    },
    closeDetail(state) {
      state.detailItem = null;
      state.detailPickedImage = null;
    },
    setDetailPickedImage(state, action: PayloadAction<string | null>) {
      state.detailPickedImage = action.payload;
    },

    // Add / Edit form
    openAddForm(state) {
      state.formMode = "add";
      state.formItemId = null;
      state.formDraft = emptyDraft();
      state.formOpen = true;
    },
    openEditForm(state, action: PayloadAction<MenuItem>) {
      state.formMode = "edit";
      state.formItemId = action.payload.id;
      state.formItemUpdatedAt = action.payload.updatedAt ?? null;
      state.formDraft = draftFromItem(action.payload);
      state.formOpen = true;
      state.detailItem = null;
    },
    closeForm(state) {
      state.formOpen = false;
      state.lightboxOpen = false;
    },
    setFormField(
      state,
      action: PayloadAction<{
        field: "menuName" | "courseName" | "description" | "price" | "calories";
        value: string;
      }>,
    ) {
      state.formDraft[action.payload.field] = action.payload.value;
    },
    setFormUnit(state, action: PayloadAction<string>) {
      state.formDraft.unit = action.payload;
    },
    setFormIngredients(state, action: PayloadAction<Ingredient[]>) {
      state.formDraft.ingredients = action.payload;
    },
    setFormCertifications(state, action: PayloadAction<Certification[]>) {
      state.formDraft.certifications = action.payload;
    },
    setFormActive(state, action: PayloadAction<boolean>) {
      state.formDraft.active = action.payload;
    },
    setFormPhotoPreview(state, action: PayloadAction<string | null>) {
      state.formDraft.photoPreview = action.payload;
      if (action.payload) state.formDraft.photoGradient = null;
    },
    setFormPhotoGradient(state, action: PayloadAction<string | null>) {
      state.formDraft.photoGradient = action.payload;
    },
    clearFormPhoto(state) {
      state.formDraft.photoPreview = null;
      state.formDraft.photoGradient = null;
    },
    setLightbox(state, action: PayloadAction<boolean>) {
      state.lightboxOpen = action.payload;
    },

    // Draft drawer
    openDraftDrawer(state) {
      state.draftOpen = true;
    },
    closeDraftDrawer(state) {
      state.draftOpen = false;
    },

    // Delete
    requestDelete(
      state,
      action: PayloadAction<
        string | { id: string; kind: "menu" | "package" | "addon" }
      >,
    ) {
      if (typeof action.payload === "string") {
        state.deleteItemId = action.payload;
        state.deleteKind = null;
      } else {
        state.deleteItemId = action.payload.id;
        state.deleteKind = action.payload.kind;
      }
      state.draftOpen = false;
      state.pkgDraftOpen = false;
      state.addOnDraftOpen = false;
      state.deleteOpen = true;
    },
    closeDelete(state) {
      state.deleteOpen = false;
      state.deleteItemId = null;
      state.deleteKind = null;
    },
    /**
     * Close the delete confirmation dialog after the API delete has completed.
     *
     * All three entity types (menu, package, addon) now go through real DELETE
     * endpoints in their respective hooks. This reducer only closes the dialog
     * and clears the delete state — it no longer removes items from the slice
     * optimistically, because the hooks refetch the full list after success.
     */
    confirmDelete(state) {
      state.deleteOpen = false;
      state.deleteItemId = null;
      state.deleteKind = null;
    },

    // Package form (3-step: Package Info → Menu & Courses → Pricing & Review)
    openAddPackageForm(state) {
      state.pkgFormMode = "add";
      state.pkgFormItemId = null;
      state.pkgFormStep = 0;
      state.pkgFormDraft = emptyPkgDraft();
      state.pkgFormOpen = true;
    },
    openEditPackageForm(state, action: PayloadAction<PackageItem>) {
      state.pkgFormMode = "edit";
      state.pkgFormItemId = action.payload.id;
      // Record the last-read updated_at for optimistic-concurrency on PATCH.
      state.pkgFormItemUpdatedAt = action.payload.updatedAt ?? null;
      state.pkgFormStep = 0;
      state.pkgFormDraft = pkgDraftFromItem(action.payload);
      state.pkgFormOpen = true;
    },
    closePkgForm(state) {
      state.pkgFormOpen = false;
      state.pkgLightboxOpen = false;
    },
    openPkgDraftDrawer(state) {
      state.pkgFormOpen = false;
      state.pkgDraftOpen = true;
    },
    closePkgDraftDrawer(state) {
      state.pkgDraftOpen = false;
    },
    openPackageDetail(state, action: PayloadAction<PackageItem>) {
      state.packageDetailItem = action.payload;
      // If we opened from the list summary (no menuStructure yet), mark the
      // detail as loading so the page can show a skeleton while it hydrates.
      state.packageDetailLoading = action.payload.menuStructure === undefined;
    },
    /**
     * Merge the background-fetched full detail into the open package (only when
     * it's still the same package on screen), then clear the loading flag. A
     * null payload just clears loading (the fetch failed → keep the summary).
     */
    patchPackageDetail(state, action: PayloadAction<PackageItem | null>) {
      const detail = action.payload;
      if (detail && state.packageDetailItem?.id === detail.id) {
        state.packageDetailItem = detail;
      }
      state.packageDetailLoading = false;
    },
    closePackageDetail(state) {
      state.packageDetailItem = null;
      state.packageDetailLoading = false;
    },
    setPkgFormStep(state, action: PayloadAction<number>) {
      state.pkgFormStep = action.payload;
    },
    setPkgFormField(
      state,
      action: PayloadAction<{
        field:
          | "name"
          | "category"
          | "venues"
          | "description"
          | "pricingType"
          | "basePricePerson"
          | "flatBaseFee";
        value: string;
      }>,
    ) {
      state.pkgFormDraft[action.payload.field] = action.payload.value;
    },
    setPkgFormCourses(state, action: PayloadAction<PackageCourseSection[]>) {
      state.pkgFormDraft.courses = action.payload;
    },
    addPkgCourse(state) {
      const newId = String(state.pkgFormDraft.courses.length + 1);
      state.pkgFormDraft.courses.push({
        id: newId,
        courseName: "",
        items: [],
      });
    },
    removePkgCourse(state, action: PayloadAction<string>) {
      state.pkgFormDraft.courses = state.pkgFormDraft.courses.filter(
        (c) => c.id !== action.payload,
      );
    },
    setPkgCourseField(
      state,
      action: PayloadAction<{
        courseId: string;
        field: "courseName";
        value: string;
      }>,
    ) {
      const course = state.pkgFormDraft.courses.find(
        (c) => c.id === action.payload.courseId,
      );
      if (course) course[action.payload.field] = action.payload.value;
    },
    addPkgCourseItem(
      state,
      action: PayloadAction<{
        courseId: string;
        item: PackageCourseSection["items"][number];
      }>,
    ) {
      const course = state.pkgFormDraft.courses.find(
        (c) => c.id === action.payload.courseId,
      );
      if (
        course &&
        !course.items.some((i) => i.id === action.payload.item.id)
      ) {
        course.items.push(action.payload.item);
      }
    },
    removePkgCourseItem(
      state,
      action: PayloadAction<{ courseId: string; itemId: string }>,
    ) {
      const course = state.pkgFormDraft.courses.find(
        (c) => c.id === action.payload.courseId,
      );
      if (course) {
        course.items = course.items.filter(
          (i) => i.id !== action.payload.itemId,
        );
      }
    },
    setPkgFormActive(state, action: PayloadAction<boolean>) {
      state.pkgFormDraft.active = action.payload;
    },
    setPkgFormPhotoPreview(state, action: PayloadAction<string | null>) {
      state.pkgFormDraft.photoPreview = action.payload;
      if (action.payload) state.pkgFormDraft.photoGradient = null;
    },
    clearPkgFormPhoto(state) {
      state.pkgFormDraft.photoPreview = null;
      state.pkgFormDraft.photoGradient = null;
    },
    setPkgLightbox(state, action: PayloadAction<boolean>) {
      state.pkgLightboxOpen = action.payload;
    },
    submitPackageForm(state) {
      if (state.pkgFormMode === "edit" && state.pkgFormItemId) {
        const updated = pkgFromDraft(state.pkgFormDraft, state.pkgFormItemId);
        const idx = state.packages.findIndex(
          (p) => p.id === state.pkgFormItemId,
        );
        if (idx !== -1) state.packages[idx] = updated;
      } else {
        const newPkg = pkgFromDraft(
          state.pkgFormDraft,
          `pkg-${state.packages.length + 1}`,
        );
        state.packages.push(newPkg);
      }
      state.pkgFormOpen = false;
      state.pkgLightboxOpen = false;
    },

    // Placeholder
    openPlaceholder(state) {
      state.placeholderOpen = true;
    },
    closePlaceholder(state) {
      state.placeholderOpen = false;
    },

    // Add-on form
    openAddAddOnForm(state) {
      state.addOnFormMode = "add";
      state.addOnFormItemId = null;
      state.addOnFormDraft = emptyAddOnDraft();
      state.addOnFormOpen = true;
    },
    openEditAddOnForm(state, action: PayloadAction<AddOnItem>) {
      state.addOnFormMode = "edit";
      state.addOnFormItemId = action.payload.id;
      // Record the last-read updated_at for optimistic-concurrency on PATCH.
      state.addOnFormItemUpdatedAt = action.payload.updatedAt ?? null;
      state.addOnFormDraft = addOnDraftFromItem(action.payload);
      state.addOnFormOpen = true;
      state.addOnDetailItem = null;
    },
    closeAddOnForm(state) {
      state.addOnFormOpen = false;
      state.addOnLightboxOpen = false;
    },
    setAddOnFormField(
      state,
      action: PayloadAction<{
        field: "name" | "description" | "price";
        value: string;
      }>,
    ) {
      state.addOnFormDraft[action.payload.field] = action.payload.value;
    },
    setAddOnFormActive(state, action: PayloadAction<boolean>) {
      state.addOnFormDraft.active = action.payload;
    },
    setAddOnFormPhotoPreview(state, action: PayloadAction<string>) {
      state.addOnFormDraft.photoPreview = action.payload;
      state.addOnFormDraft.photoGradient = null;
    },
    clearAddOnFormPhoto(state) {
      state.addOnFormDraft.photoPreview = null;
      state.addOnFormDraft.photoGradient = null;
    },
    setAddOnLightbox(state, action: PayloadAction<boolean>) {
      state.addOnLightboxOpen = action.payload;
    },
    submitAddOnForm(state) {
      if (state.addOnFormMode === "edit" && state.addOnFormItemId) {
        const updated = addOnFromDraft(
          state.addOnFormDraft,
          state.addOnFormItemId,
        );
        const idx = state.addOns.findIndex(
          (a) => a.id === state.addOnFormItemId,
        );
        if (idx !== -1) state.addOns[idx] = updated;
      } else {
        const newAddon = addOnFromDraft(
          state.addOnFormDraft,
          `addon-${state.addOns.length + 1}`,
        );
        state.addOns.push(newAddon);
      }
      state.addOnFormOpen = false;
      state.addOnLightboxOpen = false;
    },

    // Add-on draft drawer
    openAddOnDraftDrawer(state) {
      state.addOnFormOpen = false;
      state.addOnDraftOpen = true;
    },
    closeAddOnDraftDrawer(state) {
      state.addOnDraftOpen = false;
    },

    // Add-on detail
    openAddOnDetail(state, action: PayloadAction<AddOnItem>) {
      state.addOnDetailItem = action.payload;
    },
    closeAddOnDetail(state) {
      state.addOnDetailItem = null;
    },
  },
});

export const {
  setActiveTab,
  setSearch,
  setFilter,
  setMenuLoading,
  setCourses,
  setMenuItems,
  upsertMenuItem,
  setPackages,
  upsertPackage,
  setPackagesLoading,
  setPackageCategories,
  setVenues,
  setAddOns,
  upsertAddOn,
  setAddOnsLoading,
  openDetail,
  closeDetail,
  setDetailPickedImage,
  openAddForm,
  openEditForm,
  closeForm,
  setFormField,
  setFormUnit,
  setFormIngredients,
  setFormCertifications,
  setFormActive,
  setFormPhotoPreview,
  setFormPhotoGradient,
  clearFormPhoto,
  setLightbox,
  openDraftDrawer,
  closeDraftDrawer,
  requestDelete,
  closeDelete,
  confirmDelete,
  openAddPackageForm,
  openEditPackageForm,
  closePkgForm,
  openPkgDraftDrawer,
  closePkgDraftDrawer,
  openPackageDetail,
  patchPackageDetail,
  closePackageDetail,
  setPkgFormStep,
  setPkgFormField,
  setPkgFormCourses,
  addPkgCourse,
  removePkgCourse,
  setPkgCourseField,
  addPkgCourseItem,
  removePkgCourseItem,
  setPkgFormActive,
  setPkgFormPhotoPreview,
  clearPkgFormPhoto,
  setPkgLightbox,
  submitPackageForm,
  openPlaceholder,
  closePlaceholder,
  openAddAddOnForm,
  openEditAddOnForm,
  closeAddOnForm,
  setAddOnFormField,
  setAddOnFormActive,
  setAddOnFormPhotoPreview,
  clearAddOnFormPhoto,
  setAddOnLightbox,
  submitAddOnForm,
  openAddOnDraftDrawer,
  closeAddOnDraftDrawer,
  openAddOnDetail,
  closeAddOnDetail,
} = menuPackagesSlice.actions;

export default menuPackagesSlice.reducer;
